/**
 * What fills the map area when there is no map.
 *
 * Three ways that happens: no API key configured, a key Google rejected, or no
 * connection. None of them should produce a blank screen, because the demand model does
 * not depend on Google at all — the ranked list below is computed entirely from the
 * precached bundle and is still the answer to "where do I go next". The map is how you
 * read that answer quickly, not the answer itself.
 */

export type UnavailableReason = 'no-key' | 'auth' | 'network';

interface Props {
  reason: UnavailableReason;
}

export function MapUnavailable({ reason }: Props): JSX.Element {
  return (
    <div className="map-unavailable" role="status">
      <div className="map-unavailable-inner">
        {reason === 'no-key' && (
          <>
            <h2>Add a Google Maps key to see the map</h2>
            <p>
              The rankings below are working — they are computed on this device and need
              no network. Only the map underneath them needs a key.
            </p>
            <ol>
              <li>
                Create a key in the Google Cloud console and enable{' '}
                <strong>Maps JavaScript API</strong>.
              </li>
              <li>
                Put it in <code>.env.local</code> at the repo root:
                <br />
                <code>VITE_GOOGLE_MAPS_API_KEY=your-key</code>
              </li>
              <li>Restart the dev server, or rebuild.</li>
            </ol>
            <p className="map-unavailable-footnote">
              Restrict the key by HTTP referrer and to the Maps JavaScript API. Browser
              map keys are necessarily public — restriction is what protects them, not
              secrecy.
            </p>
          </>
        )}

        {reason === 'auth' && (
          <>
            <h2>Google rejected the key</h2>
            <p>
              Usually one of three things: the <strong>Maps JavaScript API</strong> is not
              enabled for the project, billing is not set up, or the key&rsquo;s referrer
              restrictions do not cover the address you are loading from.
            </p>
            <p>The rankings below are unaffected and still live.</p>
          </>
        )}

        {reason === 'network' && (
          <>
            <h2>No connection to Google</h2>
            <p>
              The map needs a network; Google&rsquo;s terms do not allow caching their
              tiles for offline use, so there is nothing stored to fall back on.
            </p>
            <p>
              The rankings below still work — the venue data and the model are on this
              device. You lose the streets, not the answer.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
